<script setup lang="ts">
import type { GlobalCollectionId, GlobalPrimitiveFieldId, PrimitiveType } from '#layers/data-model/types/schema';
import type { ResolvedMonospacePrimitiveField } from '#layers/data-model/types/schema/resolved-schema';
import { computed, ref } from 'vue';
import { useAppConfig } from '#app/config';
import { useDisplayNames } from '#imports';
import { useCollection } from '#layers/data-model/composables/use-collection';
import { getPrimitiveFieldPrimaryKeyInfo, getPrimitiveFieldUniqueInfo } from '#layers/data-model/utils/schema/collection';
import { getTypename } from '#layers/data-model/utils/schema/primitive-type';
import { toDbSafeValue, WithScope } from '#layers/ui';

interface CreatedField {
	readonly id: GlobalPrimitiveFieldId;
	readonly dbName: string;
}

const props = withDefaults(defineProps<{
	collectionId: GlobalCollectionId;
	createdFields?: CreatedField[];
	allowCreate?: boolean;
	disabled?: boolean;
	filter?: (field: ResolvedMonospacePrimitiveField) => boolean;
	label?: 'dbName' | 'apiName' | 'displayName';
	showFieldInformation?: boolean;
	clear?: boolean;
}>(), {
	label: 'displayName',
	createdFields: () => [],
});

const emit = defineEmits<{
	create: [name: string];
}>();

const modelValue = defineModel<GlobalPrimitiveFieldId | null>({ required: true });
const searchTerm = ref('');

const appConfig = useAppConfig();
const { collection } = useCollection(() => props.collectionId);

const fields = computed(() =>
	collection.value?.primitiveFields
		.filter((field) => props.filter?.(field) ?? true) ?? []);

const items = computed(() => [...fields.value, ...props.createdFields]
	.map((field) => ({
		...field,
		uniqueInfo: isPrimitiveField(field) ? getPrimitiveFieldUniqueInfo(field) : { isUnique: false },
		pkInfo: isPrimitiveField(field) ? getPrimitiveFieldPrimaryKeyInfo(field) : { isPrimaryKey: false },
		label: getItemLabel(field),
		ui: !isPrimitiveField(field)
			? {
					itemLabel: 'text-primary',
				}
			: undefined,
	}))
	.toSorted((a, b) => a.label.localeCompare(b.label))
	.toSorted((a, b) => Number(b.pkInfo.isPrimaryKey) - Number(a.pkInfo.isPrimaryKey)),
);

const selectedValueIsCreated = computed(() => isValueNewlyCreated(modelValue.value));
const normalizedCreateValue = computed(() => toDbSafeValue(searchTerm.value) ?? '');
const canCreate = computed(() =>
	!!props.allowCreate
	&& normalizedCreateValue.value !== ''
	&& !items.value.some((item) => item.dbName === normalizedCreateValue.value),
);

const fontStyle = computed(() => props.label === 'displayName' ? '' : 'font-medium font-mono');

const { getPrimitiveFieldDisplayName } = useDisplayNames();

function getItemLabel(field: ResolvedMonospacePrimitiveField | CreatedField) {
	if (!isPrimitiveField(field)) return field.dbName;
	if (props.label === 'displayName') return getPrimitiveFieldDisplayName(field);

	return field[props.label];
}

function getModelValueItem(value: GlobalPrimitiveFieldId | null) {
	return items.value.find((item) => item.id === value);
}

function isPrimitiveField(field: ResolvedMonospacePrimitiveField | CreatedField): field is ResolvedMonospacePrimitiveField {
	return 'type' in field;
}

function isValueNewlyCreated(value: GlobalPrimitiveFieldId | null) {
	return value !== null && props.createdFields?.some((field) => field.id === value);
}

function handleCreate() {
	if (!canCreate.value) return;

	searchTerm.value = normalizedCreateValue.value;
	emit('create', normalizedCreateValue.value);
}
</script>

<template>
	<DSelectMenu
		v-model="modelValue as GlobalPrimitiveFieldId | undefined"
		v-model:search-term="searchTerm"
		value-key="id"
		:filter-fields="['dbName', 'apiName', 'label']"
		:create-item="canCreate ? 'always' : undefined"
		:items
		:disabled
		:clear
		:ui="{
			base: [fontStyle, 'overflow-hidden', selectedValueIsCreated ? 'text-primary' : undefined],
			item: fontStyle,
			itemLabel: 'flex items-center gap-1.5',
		}"
		@create="handleCreate"
	>
		<template #create-item-label>
			{{ normalizedCreateValue }}
		</template>

		<template #default="{ modelValue: selectedValue }">
			<WithScope v-if="selectedValue" v-slot="{ item }" :define="{ item: getModelValueItem(selectedValue) }">
				<span class="truncate">
					{{ item?.label }}
				</span>
				<DBadge
					v-if="isValueNewlyCreated(selectedValue)"
					size="xs"
					color="primary"
					variant="subtle"
					class="whitespace-nowrap"
				>
					{{ $t('field_select.new_field') }}
				</DBadge>
			</WithScope>
			<span v-else>
				{{ $t('field_select.placeholder') }}
			</span>
		</template>

		<template v-if="showFieldInformation" #item-label="{ item: field }">
			<span :class="{ 'text-primary': isValueNewlyCreated(field.id) }">{{ field.label }}</span>
			<DIcon v-if="field.pkInfo.isPrimaryKey" :name="appConfig.ui.icons.primaryKey" class="text-primary" />
			<DIcon v-if="field.uniqueInfo.isUnique" :name="appConfig.ui.icons.unique" class="text-dimmed" />
		</template>

		<template v-if="showFieldInformation" #item-trailing="{ item: field }">
			<span v-if="'type' in field && field.type" class="ml-4 font-normal text-dimmed">{{ getTypename(field.type as PrimitiveType) }}</span>
		</template>
	</DSelectMenu>
</template>
